import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ScreenContainer, ContentFrame } from '@pinwheel/origami';
import { useAppDispatch, useAppSelector } from '../../state';
import { initializeDDS, next, selectJobType, selectLinkApiResponse, selectSelectedMaskedAccountId, waitForJobStepResults, } from '../../state/slices/slice-link-api';
import LoadingView from '../../views/Loading';
import JobTemplate from '../../LinkApiRouter/views/JobTemplate';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import ModalHeader from '../../components/ModalHeader';
import { selectAmountSelection } from '../../state/slices/slice-amount-selection';
import useIsLinkApiLoading from '../../hooks/useIsLinkApiLoading';
import { setError } from '../../state/slices/slice-error';
import { useOpts } from '../../context';
import useFireClientEvent from '../../hooks/useFireClientEvent';
import { createInputAllocationPayload, createInputAmountPayloadLegacy, } from '../../events/utils';
import { tokenHasJob } from '../../utils';
import { updateEventSource } from '../../state/slices/slice-segment';
import eventQueue from '../../events/queue';
import { EventSurface } from '../../types/events';
import isGuidedSwitch from '../../LinkApiRouter/utils/guided-switch';
import useUserEnteredBankInfo from '../../hooks/useUserEnteredBankInfo';
function getEventSource(linkApiResponse, doGuidedSwitch) {
    if (linkApiResponse.responseType === 'success') {
        return null;
    }
    if (doGuidedSwitch) {
        return EventSurface.GUIDED_SWITCH;
    }
    return null;
}
var DDS = function () {
    var _a = useOpts(), featureFlags = _a.featureFlags, loadedPlatform = _a.loadedPlatform, linkTokenPayload = _a.linkTokenPayload, exit = _a.exit;
    var dispatch = useAppDispatch();
    var navigate = useLinkNavigate();
    var linkApiResponse = useSelector(selectLinkApiResponse);
    var amountSelection = useSelector(selectAmountSelection);
    var jobType = useSelector(selectJobType);
    var maskedAccountId = useAppSelector(selectSelectedMaskedAccountId);
    var bankInfo = useUserEnteredBankInfo();
    var doCardSwitch = featureFlags.cardSwitch &&
        ((linkTokenPayload && tokenHasJob(linkTokenPayload, 'card_switch')) ||
            (linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.enableCardSwitch)) &&
        loadedPlatform.supportedJobs.some(function (job) { return job.job === 'card_switch' && job.enabled; });
    var doGuidedSwitch = useMemo(function () {
        return isGuidedSwitch(loadedPlatform, featureFlags) || false;
    }, [loadedPlatform, featureFlags]);
    var fireClientEvent = useFireClientEvent();
    useEffect(function () {
        fireClientEvent('input_amount', createInputAmountPayloadLegacy(amountSelection));
        fireClientEvent('input_allocation', createInputAllocationPayload(amountSelection));
        void dispatch(initializeDDS({ amountSelection: amountSelection, bankInfo: bankInfo, maskedAccountId: maskedAccountId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // emit an impression event on each new screen; only applies to guided switch case
    useEffect(function () {
        if (!linkApiResponse)
            return;
        var eventSource = getEventSource(linkApiResponse, doGuidedSwitch);
        if (eventSource) {
            dispatch(updateEventSource(eventSource));
            eventQueue.emit('impression', {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkApiResponse]);
    useEffect(function () {
        if (jobType !== 'direct_deposit_switch') {
            return;
        }
        if (!linkApiResponse)
            return;
        if (linkApiResponse.responseType === 'next')
            return;
        if (linkApiResponse.responseType === 'processing') {
            void dispatch(waitForJobStepResults());
        }
        else if (doCardSwitch) {
            navigate('/card-switch/confirm');
        }
        else if (linkApiResponse.responseType === 'success') {
            if ((featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.skipSuccessOrErrorTerminalScreen) ||
                (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.skipSuccessScreen))
                exit();
            else
                navigate('/success');
        }
        else if (linkApiResponse.responseType === 'pending_retry') {
            dispatch(setError({
                type: linkApiResponse.error.type,
                code: linkApiResponse.error.code,
                message: linkApiResponse.error.message,
                pendingRetry: true,
            }));
            if (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.skipSuccessOrErrorTerminalScreen) {
                exit();
            }
            else {
                navigate('/error');
            }
        }
        else if (linkApiResponse.responseType === 'error' ||
            linkApiResponse.responseType === 'next_with_error') {
            dispatch(setError({
                type: linkApiResponse.error.type,
                code: linkApiResponse.error.code,
                message: linkApiResponse.error.message,
                pendingRetry: false,
            }));
            if (linkApiResponse.responseType === 'error') {
                // Disable DD forms in skipDepositConfirmation case
                if (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.skipSuccessOrErrorTerminalScreen) {
                    exit();
                }
                else {
                    navigate('/error');
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkApiResponse]);
    var isLoading = useIsLinkApiLoading();
    var willRouteToError = linkApiResponse &&
        (linkApiResponse.responseType === 'error' ||
            linkApiResponse.responseType === 'pending_retry');
    return isLoading || willRouteToError ? (React.createElement(LoadingView, { hideModalHeader: true })) : (React.createElement(ScreenContainer, { "data-testid": "linkApi/dds" },
        React.createElement(ModalHeader, { showPlatformLogo: true }),
        React.createElement(ContentFrame, null,
            React.createElement(JobTemplate, { linkApiResponse: linkApiResponse, submitStep: function (payload) {
                    return dispatch(next({
                        payload: payload,
                    }));
                } }))));
};
export default DDS;
